import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Booking.css';

function Booking() {
  const form = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    projectType: '',
    startDate: '',
    duration: '',
    additionalInfo: '',
    sameAsDelivery: false,
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZipCode: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
  
    try {
      // Create a FormData object
      const formData = new FormData(e.target);
      
      // If same as delivery is checked, manually add billing info from delivery
      if (e.target.sameAsDelivery.checked) {
        formData.set('billingAddress', e.target.address.value);
        formData.set('billingCity', e.target.city.value);
        formData.set('billingState', e.target.state.value);
        formData.set('billingZipCode', e.target.zipCode.value);
      }
      
      // Create a temporary form to send
      const tempForm = document.createElement('form');
      formData.forEach((value, key) => {
        const input = document.createElement('input');
        input.name = key;
        input.value = value;
        tempForm.appendChild(input);
      });
      
     // Send emails - both company notification and customer auto-reply
      emailjs.sendForm(
        'service_xhkik8j',
        'template_72wabtq',
        tempForm,
        'JjDr-XBM40wFmJ4NQ'
      )
      .then((result) => {
        console.log('Emails sent successfully:', result.text);
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          projectType: '',
          startDate: '',
          duration: '',
          additionalInfo: '',
          sameAsDelivery: false,
          billingAddress: '',
          billingCity: '',
          billingState: '',
          billingZipCode: ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((error) => {
        console.error('Email send failed:', error);
        setSubmitStatus('error');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Create new form data state
    const newFormData = { ...formData };
    
    // Handle checkbox separately
    if (type === 'checkbox') {
      newFormData[name] = checked;
      
      // If checkbox is checked, copy delivery address to billing fields
      if (name === 'sameAsDelivery') {
        if (checked) {
          newFormData.billingAddress = formData.address;
          newFormData.billingCity = formData.city;
          newFormData.billingState = formData.state;
          newFormData.billingZipCode = formData.zipCode;
        } else {
          // Clear billing fields when unchecking
          newFormData.billingAddress = '';
          newFormData.billingCity = '';
          newFormData.billingState = '';
          newFormData.billingZipCode = '';
        }
      }
    } else {
      newFormData[name] = value;
      
      // If updating delivery address and checkbox is checked, update billing address too
      if (
        formData.sameAsDelivery && 
        ['address', 'city', 'state', 'zipCode'].includes(name)
      ) {
        const billingField = 'billing' + name.charAt(0).toUpperCase() + name.slice(1);
        newFormData[billingField] = value;
      }
    }
    
    setFormData(newFormData);
  };

  return (
    <div className="booking-page">
      <div className="quote-form">
        <h1 className="form-title">Get Your Quote Today</h1>
        <p className="form-subtitle">Fill out the form below and we'll get back to you within 24 hours</p>
        
        {submitStatus === 'success' && (
          <div className="alert alert-success">
            Thank you for your quote request! We'll get back to you within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="alert alert-error">
            Sorry, there was an error submitting your request. Please try again or contact us directly.
          </div>
        )}
        
        <form ref={form} onSubmit={handleSubmit}>
          {/* Contact Information */}
          <div className="form-section">
            <h2>Contact Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Phone Number <span className="required">*</span></label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-divider"></div>

          {/* Delivery Information */}
          <div className="form-section">
            <h2>Delivery Information</h2>
            <div className="form-group">
              <label>Street Address <span className="required">*</span></label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>City <span className="required">*</span></label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>State <span className="required">*</span></label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select State</option>
                  <option value="AL">Alabama</option>
                  {/* Add other states as needed */}
                </select>
              </div>
              <div className="form-group">
                <label>ZIP Code <span className="required">*</span></label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-divider"></div>

          {/* Billing Information */}
          <div className="form-section">
            <h2>Billing Information</h2>
            <p className="section-note">Please provide your billing information or check the box if it's the same as your delivery address.</p>
            <div className="checkbox-container">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  name="sameAsDelivery" 
                  checked={formData.sameAsDelivery}
                  onChange={handleChange}
                  className="checkbox-input"
                  style={{
                    marginRight: '8px',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                Same as delivery address
              </label>
            </div>
            {!formData.sameAsDelivery && (
              <div className="billing-fields-container">
                <div className="form-group">
                  <label>Billing Address <span className="required">*</span></label>
                  <input
                    type="text"
                    name="billingAddress"
                    value={formData.billingAddress}
                    onChange={handleChange}
                    required={!formData.sameAsDelivery}
                  />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>City <span className="required">*</span></label>
                    <input
                      type="text"
                      name="billingCity"
                      value={formData.billingCity}
                      onChange={handleChange}
                      required={!formData.sameAsDelivery}
                    />
                  </div>
                  <div className="form-group">
                    <label>State <span className="required">*</span></label>
                    <select
                      name="billingState"
                      value={formData.billingState}
                      onChange={handleChange}
                      required={!formData.sameAsDelivery}
                    >
                      <option value="">Select State</option>
                      <option value="AL">Alabama</option>
                      {/* Add other states as needed */}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>ZIP Code <span className="required">*</span></label>
                    <input
                      type="text"
                      name="billingZipCode"
                      value={formData.billingZipCode}
                      onChange={handleChange}
                      required={!formData.sameAsDelivery}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-divider"></div>

          {/* Project Details */}
          <div className="form-section">
            <h2>Project Details</h2>
            <div className="form-group">
              <label>Project Type</label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
              >
                <option value="">Select Project Type</option>
                <option value="construction">Construction</option>
                <option value="renovation">Renovation</option>
                <option value="landscaping">Landscaping</option>
                <option value="cleanout">Property Cleanout</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Rental Duration</label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                >
                  <option value="">Select Duration</option>
                  <option value="5-days">5 Days</option>
                  <option value="10-days">10 Days</option>
                  <option value="custom">Custom Duration</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Additional Information</label>
              <textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows="4"
                placeholder="Please provide any additional details about your project..."
              ></textarea>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Get Your Quote'}
          </button>
        </form>
      </div>

      {/* Additional Information Section */}
      <div className="additional-info">
        <div className="info-grid">
          <div className="info-card">
            <h3>What to Expect</h3>
            <p>After submitting your quote request, our team will review your details and contact you within 24 hours with pricing and availability.</p>
          </div>
          <div className="info-card">
            <h3>Pricing Information</h3>
            <p>Our standard rental includes delivery, 5-day or 10-day rental period, and pickup. Additional fees may apply for overweight loads or extended rental periods.</p>
          </div>
          <div className="info-card">
            <h3>Service Area</h3>
            <p>We currently service the greater Muscle Shoals, Decatur, Florence and surrounding areas. Contact us to confirm service availability in your location.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Booking;
